import { FETCH_REPORTS_ERROR, FETCH_REPORTS_SUCCESS } from '@core/redux/reports/actions';
import { Api, Models } from '@core/types';
import { ReportType } from '@core/types/api';
import { reportActionCreators } from '@redux/reports';
import { PassportEnums, usePassportContext } from '@tti/passport';
import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Column, TableOptions } from 'react-table';
import DateFilters from './DateFilters';
import Table from './Table';
import TemplateFilters from './TemplateFilters';

interface IProps {
  fetchReports: (params: Api.IFetchReportsRequest) => void;
  downloadPrintsByTemplateAndCultureCsv: (params: {
    bearerToken: string;
    dateFrom?: string;
    dateTo?: string;
    templateNames?: string[];
  }) => void;
  downloadDownloadsByTemplateAndCultureCsv: (params: {
    bearerToken: string;
    dateFrom?: string;
    dateTo?: string;
    templateNames?: string[];
  }) => void;
}

const ReportsTable: FunctionComponent<IProps> = ({
  fetchReports,
  downloadPrintsByTemplateAndCultureCsv,
  downloadDownloadsByTemplateAndCultureCsv,
}) => {
  const { passportContext, getClaims } = usePassportContext();
  const reportingClaims = getClaims(PassportEnums.ClaimType.Role, passportContext.claims).filter((x) =>
    x.value.startsWith(PassportEnums.RoleType.ReportingAccess),
  );

  const [reportingData, setReportingData] = useState<Api.IFetchReportsResponse>({
    printsByCulture: [],
    printsByUser: [],
    downloadsByCulture: [],
    downloadsByUser: [],
    downloadsByTemplate: [],
    printsByTemplate: [],
  });
  const [dateFrom, setDateFrom] = useState<string>();
  const [dateTo, setDateTo] = useState<string>();
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);

  const makeHeadings = (mappings: Record<string, string>): Column<Models.PrintsByUser>[] => {
    const tableHeadings: Column<Models.PrintsByUser>[] = [];
    Object.keys(mappings).forEach((key) => {
      const newHeading: any = {
        Header: mappings[key],
        accessor: key,
      };
      tableHeadings.push(newHeading);
    });
    return tableHeadings;
  };

  const getDateString = (): string => {
    if (!dateFrom && !dateTo) {
      return 'All Time';
    }

    if (dateFrom === dateTo) {
      return dateFrom as string;
    }

    if (!dateFrom) {
      return `Up to ${dateTo}`;
    }

    if (!dateTo) {
      return `From ${dateFrom}`;
    }

    return `${dateFrom} - ${dateTo}`;
  };

  const makeTableOptions = (key: string, columnMappings: Record<string, string>): TableOptions<Models.PrintsByUser> => {
    let dataToUse: any;
    switch (key) {
      case 'printsByCulture':
        dataToUse = reportingData.printsByCulture;
        break;
      case 'printsByUser':
        dataToUse = reportingData.printsByUser;
        break;
      case 'downloadsByCulture':
        dataToUse = reportingData.downloadsByCulture;
        break;
      case 'downloadsByUser':
        dataToUse = reportingData.downloadsByUser;
        break;
      case 'downloadsByTemplate':
        dataToUse = reportingData.downloadsByTemplate;
        break;
      case 'printsByTemplate':
        dataToUse = reportingData.printsByTemplate;
        break;
    }
    return {
      columns: makeHeadings(columnMappings),
      data: dataToUse,
    };
  };

  useEffect(() => {
    if (!passportContext.bearerToken) return;

    async function getReports() {
      const result: any = fetchReports({
        bearerToken: passportContext.bearerToken || '',
        dateFrom: dateFrom,
        dateTo: dateTo,
        templateNames: selectedTemplates.length > 0 ? selectedTemplates : undefined,
      });

      result.then((x: any) => {
        if (x.type === FETCH_REPORTS_SUCCESS) {
          setReportingData(x?.payload);
        } else if (x.type === FETCH_REPORTS_ERROR) {
          console.error('Error: ', x);
        }
      });
    }

    getReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, selectedTemplates]);

  if (!reportingClaims) {
    return null;
  }

  return (
    <Fragment>
      <h1>REPORTING</h1>
      <h2>Filters</h2>
      <DateFilters dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
      <TemplateFilters selectedTemplates={selectedTemplates} setSelectedTemplates={setSelectedTemplates} />

      <hr />

      <div className="table__container">
        <h3>Monthly Usage CSV ({getDateString()})</h3>
        <p>
          <button
            className="btn btn--inline btn--small"
            onClick={() =>
              downloadPrintsByTemplateAndCultureCsv({
                bearerToken: passportContext.bearerToken || '',
                dateFrom,
                dateTo,
                templateNames: selectedTemplates.length > 0 ? selectedTemplates : undefined,
              })
            }
          >
            Prints
          </button>
          <button
            className="btn btn--inline btn--small"
            onClick={() =>
              downloadDownloadsByTemplateAndCultureCsv({
                bearerToken: passportContext.bearerToken || '',
                dateFrom,
                dateTo,
                templateNames: selectedTemplates.length > 0 ? selectedTemplates : undefined,
              })
            }
          >
            Downloads
          </button>
        </p>
      </div>
      <br />
      {reportingData && (
        <>
          {/* Prints */}
          <div className="table__container">
            <h3>Prints By Culture ({getDateString()})</h3>
            <Table
              options={makeTableOptions('printsByCulture', {
                region: 'Region',
                cultureCode: 'Language',
                totalWaitingApproval: 'Total Projects Awaiting Approval',
                totalRejected: 'Total Projects Rejected',
                totalApproved: 'Total Projects Approved',
                totalTagsApproved: 'Total Tags Approved',
              })}
              reportType={ReportType.PrintsByCulture}
              dateTo={dateTo}
              dateFrom={dateFrom}
            />
          </div>
          <div className="table__container">
            <h3>Prints By User ({getDateString()})</h3>
            <Table
              options={makeTableOptions('printsByUser', {
                firstName: 'First Name',
                lastName: 'Last Name',
                cultureCode: 'Culture Code',
                totalWaitingApproval: 'Total Projects Awaiting Approval',
                totalRejected: 'Total Projects Rejected',
                totalApproved: 'Total Projects Approved',
                totalTagsApproved: 'Total Tags Approved',
              })}
              reportType={ReportType.PrintsByUser}
              dateTo={dateTo}
              dateFrom={dateFrom}
            />
          </div>
          <div className="table__container">
            <h3>Prints by Template ({getDateString()})</h3>
            <Table
              options={makeTableOptions('printsByTemplate', {
                templateName: 'Template Name',
                totalWaitingApproval: 'Total Projects Awaiting Approval',
                totalRejected: 'Total Projects Rejected',
                totalApproved: 'Total Projects Approved',
                totalTagsApproved: 'Total Tags Approved',
              })}
              reportType={ReportType.PrintsByTemplate}
              dateTo={dateTo}
              dateFrom={dateFrom}
            />
          </div>
          {/* Downloads */}
          <div className="table__container">
            <h3>Downloads By Culture ({getDateString()})</h3>
            <Table
              options={makeTableOptions('downloadsByCulture', {
                cultureCode: 'Culture Code',
                totalDownloads: 'Total Projects Downloaded',
              })}
              reportType={ReportType.DownloadsByCulture}
              dateTo={dateTo}
              dateFrom={dateFrom}
            />
          </div>
          <div className="table__container">
            <h3>Downloads By User ({getDateString()})</h3>
            <Table
              options={makeTableOptions('downloadsByUser', {
                firstName: 'First Name',
                lastName: 'Last Name',
                cultureCode: 'Culture Code',
                totalDownloads: 'Total Projects Downloaded',
              })}
              reportType={ReportType.DownloadsByUser}
              dateTo={dateTo}
              dateFrom={dateFrom}
            />
          </div>
          <div className="table__container">
            <h3>Downloads By Template ({getDateString()})</h3>
            <Table
              options={makeTableOptions('downloadsByTemplate', {
                templateName: 'Template Name',
                totalDownloads: 'Total Projects Downloaded',
              })}
              reportType={ReportType.DownloadsByTemplate}
              dateTo={dateTo}
              dateFrom={dateFrom}
            />
          </div>
        </>
      )}
    </Fragment>
  );
};

const mapDispatchToProps = {
  fetchReports: (params: Api.IFetchReportsRequest) => reportActionCreators.fetchReports(params),
  downloadPrintsByTemplateAndCultureCsv: (params: {
    bearerToken: string;
    dateFrom?: string;
    dateTo?: string;
    templateNames?: string[];
  }) => reportActionCreators.downloadPrintsByTemplateAndCultureCsv(params),
  downloadDownloadsByTemplateAndCultureCsv: (params: {
    bearerToken: string;
    dateFrom?: string;
    dateTo?: string;
    templateNames?: string[];
  }) => reportActionCreators.downloadDownloadsByTemplateAndCultureCsv(params),
};

export default connect(null, mapDispatchToProps)(ReportsTable);
